import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnDestroy,
    viewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DomSanitizer} from '@angular/platform-browser';
import {filter, map, startWith} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {z} from 'zod';
import {getLastChild, getMergedParams} from '@shared/route-utils';
import {environment} from 'src/environments/environment';

@Component({
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    template: `
        @if (path$ | async; as path) {
            <iframe #reactOnlineFrame [src]="path"></iframe>
        }
    `,
    styles: [
        `
            iframe {
                flex-grow: 1;
                border: none;
            }
        `,
    ],
})
export class ReactOnlineIframeComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly reactOnlineUrl = environment.reactOnlineUrl;
    private readonly reactOnlineOrigin = new URL(this.reactOnlineUrl, window.location.href).origin;
    private readonly reactOnlineFrame =
        viewChild<ElementRef<HTMLIFrameElement>>('reactOnlineFrame');

    private readonly route$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        filter(
            () => this.router.getCurrentNavigation()?.extras.state?.['source'] !== 'react-online',
        ),
        startWith(null),
        map(() => this.activatedRoute),
    );

    readonly path$ = this.route$.pipe(
        map(route =>
            this.sanitizer.bypassSecurityTrustResourceUrl(
                `${this.reactOnlineUrl}/${this.getReactRoute(route)}`,
            ),
        ),
    );

    constructor() {
        window.addEventListener('message', this.handleMessageEvent);
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.handleMessageEvent);
    }

    private getReactRoute(route: ActivatedRoute): string {
        const mergedParams = getMergedParams(route.root.snapshot);
        const reactRoute: unknown = getLastChild(route).snapshot.data['reactRoute'];
        return typeof reactRoute === 'string'
            ? this.replacePlaceholders(reactRoute, mergedParams)
            : '';
    }

    private replacePlaceholders(template: string, valuesMap: Map<string, string>): string {
        // This regex matches any text that is enclosed by curly braces {}, capturing everything
        // inside the braces. The [^}] ensures that it stops capturing once it encounters a closing
        // brace, and the + ensures it captures at least one character inside the braces.
        return template.replace(/{([^}]+)}/g, (match, key) => {
            const value = valuesMap.get(key);
            return value !== undefined ? value : match;
        });
    }

    private isFromReactOnlineFrame(event: MessageEvent): boolean {
        const frameWindow = this.reactOnlineFrame()?.nativeElement.contentWindow;
        return (
            frameWindow != null &&
            event.source === frameWindow &&
            event.origin === this.reactOnlineOrigin
        );
    }

    private handleMessageEvent = (event: MessageEvent): void => {
        if (!this.isFromReactOnlineFrame(event)) {
            return;
        }
        try {
            const data = eventDataSchema.parse(event.data);
            if (data.route == null) {
                return;
            }
            void this.router.navigate([data.route], {
                relativeTo: this.activatedRoute,
                state: {source: 'react-online'},
            });
        } catch (error) {
            console.error(error);
            throw new Error(`Failed to parse message event: ${JSON.stringify(event.data)}`, {
                cause: error,
            });
        }
    };
}

const eventDataSchema = z.object({
    route: z.string().optional(),
});
